// ─── clientAppMapper.js ────────────────────────────────────────────────────
// Transforme un couple (client, consultation) du SaaS Anissa en JSON
// "ClientPlan" attendu par l'app cliente premium (anissa-client-preview).
//
// ⚠️  VERSION À BLANC — étape 1
// On lit UNIQUEMENT les colonnes existantes en DB. Les champs littéraires
// que le SaaS ne stocke pas encore (pull_quote, tailored_points,
// signature_phrase, intros de section, focus du jour, hints repas) sont
// laissés undefined. L'app cliente les supporte déjà optionnels et les
// ignore si absents (cf. anissa-client-preview/types/plan.ts).
//
// Une fois ce mapping validé sur 1 vraie consultation, on ajoutera les
// colonnes manquantes côté SaaS pour enrichir l'expérience cliente.
//
// Sortie : objet conforme à `ClientPlan` (types/plan.ts), à insérer dans
// la table `client_plans` du Supabase staging app cliente.

import {
  detectSectionType,
  parseLabeledLines,
  parseBulletLines,
  parseRotationGroups,
  parseSlotAlternatives,
  normalizeSlotLabelToSlot,
  parseSupplementEntriesStructured,
} from "../nutritionEditorParsers";
import { getNutritionPlanMode } from "./nutritionPlanMode";
import { mealKey } from "./extractMealsFromPlan";
// V94.63 : parser unifie aliments interdits (partage avec FicheFrigoPreview + Word)
import { buildForbiddenList } from "./foodRestrictionsParser";
// V94.64 : builder canonique partage (coherence E2E avec modal SaaS + Word)
import { buildCanonicalFridgeData } from "./fridgeDataBuilder";
import { buildPublishedMealPrep, buildPublishedModularMealPrep } from "./mealPrep";
import { buildPublishedFourWeekRotation } from "./fourWeekRotation";
import { applySupplementEditorialOverride } from "./supplementEditorialOverrides";
import { sha256HexSync } from "./sha256Sync";

// ─── Helpers généraux ─────────────────────────────────────────────────────

const DAY_LABELS_FR = [
  "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche",
];
const DAY_SHORT_FR = ["L", "M", "M", "J", "V", "S", "D"];

const DAY_LABELS_EN = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];
const DAY_SHORT_EN = ["M", "T", "W", "T", "F", "S", "S"];

const BASE_MEAL_SLOTS = Object.freeze(["breakfast", "lunch", "dinner"]);

// Contrat éditorial produit par le choix explicite de structure des repas.
// Les ids viennent de mealStructure.fr.js ; le mapper ne les déduit jamais du
// profil. Il s'en sert uniquement pour stabiliser le DTO client et contrôler
// que le texte généré contient bien les slots qui ont été choisis.
export const MEAL_STRUCTURE_CONTRACTS = Object.freeze({
  trois_repas: Object.freeze({
    id: "trois_repas",
    expected_slots: Object.freeze([...BASE_MEAL_SLOTS]),
  }),
  collation_apres_midi: Object.freeze({
    id: "collation_apres_midi",
    expected_slots: Object.freeze([...BASE_MEAL_SLOTS, "afternoon_snack"]),
  }),
  collation_matin_apres_midi: Object.freeze({
    id: "collation_matin_apres_midi",
    expected_slots: Object.freeze([
      "breakfast", "morning_snack", "lunch", "afternoon_snack", "dinner",
    ]),
  }),
  collation_soir: Object.freeze({
    id: "collation_soir",
    expected_slots: Object.freeze([...BASE_MEAL_SLOTS, "evening_snack"]),
  }),
});

function resolveMealStructureContract(consultation) {
  const overrides = consultation?.editorial_overrides || consultation?.editorialOverrides;
  const structureId = String(overrides?.meal_structure || "").trim();
  return MEAL_STRUCTURE_CONTRACTS[structureId] || null;
}

/** Slugifier safe pour StableId (ASCII, kebab-case). */
function slugify(text) {
  return String(text || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50) || "x";
}

/** Garantir l'unicité d'un id en suffixant -1, -2, ... */
function uniqueId(base, used) {
  let id = base;
  let n = 1;
  while (used.has(id)) {
    id = `${base}-${n++}`;
  }
  used.add(id);
  return id;
}

function supplementEntryFingerprint(entry) {
  const fields = entry?.fields && typeof entry.fields === "object" ? entry.fields : {};
  const normalizedFields = Object.fromEntries(
    Object.keys(fields).sort().map((key) => [key, String(fields[key] || "").replace(/\s+/g, " ").trim()]),
  );
  return JSON.stringify({
    name: String(entry?.name || "").replace(/\s+/g, " ").trim(),
    fields: normalizedFields,
  });
}

/** Split un texte en paragraphes non vides (par lignes vides). */
function splitParagraphs(text) {
  return String(text || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

// ─── Découpage du nutrition_plan markdown en sections ─────────────────────
//
// Aligné sur structurePlanSections() de NutritionConsultation.jsx (ligne 1647)
// pour garantir le même découpage que le PDF. Détecte un titre quand :
//   - la ligne commence par #, ## ou ###
//   - OU la ligne est entièrement en MAJUSCULES (5-80 caractères)
// Le contenu avant le 1er titre est traité comme une section "Introduction"
// implicite (comportement miroir du SaaS).

function isUppercaseHeader(line) {
  const t = line.trim();
  if (!t || t.length < 5 || t.length >= 80) return false;
  // Considéré "majuscules" si le texte est strictement égal à sa version
  // upper-case (caractères accentués inclus).
  return t === t.toUpperCase() && /[A-ZÀ-Ý]/.test(t);
}

// Wrapper de classification : couvre le format Anissa standard (via
// detectSectionType) ET le format Benfitcoach sport (jours de semaine
// directs comme titres, sections stratégie multiples, "ALIMENTS À ÉVITER",
// etc.). Ne modifie pas detectSectionType d'origine — on étend localement.
const DAY_NAME_RE = /^(lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i;
const DAY_NAME_TO_INDEX = {
  lundi: 1, monday: 1,
  mardi: 2, tuesday: 2,
  mercredi: 3, wednesday: 3,
  jeudi: 4, thursday: 4,
  vendredi: 5, friday: 5,
  samedi: 6, saturday: 6,
  dimanche: 7, sunday: 7,
};
const STRATEGY_LIKE_RE = /^(besoins\s*caloriques|r[eé]partition\s*macronutriments|macronutriments|points?\s*d.attention|approche\s*anti.inflammatoire|optimisation\s*microbiote|gestion\s*[eé]nerg[eé]tique|timing\s*nutritionnel|hydratation|signaux\s*d.alarme|ajustements?\s*performance|suivi\s*recommand|examens?\s*compl|anti.inflammatoires?|soutien\s*digestif|astuces|principes?\s*nutritionnels?)/i;
const FOOD_AVOID_RE = /^(aliments?\s*[aà]\s*[eé]viter|aliments?\s*[aà]\s*limiter|forbidden\s*foods|limited\s*foods)/i;
const SHOPPING_RE = /^(liste\s*de\s*courses|shopping\s*list)/i;

function classifyTitle(title) {
  const t = String(title || "").trim();
  if (DAY_NAME_RE.test(t)) return "meals_day";
  if (FOOD_AVOID_RE.test(t)) return "food_limit";
  if (SHOPPING_RE.test(t)) return "shopping";
  // Section stratégie-like (couvre les multi-sections du format sport)
  if (STRATEGY_LIKE_RE.test(t)) return "strategy_extra";
  return detectSectionType(t);
}

function dayIndexFromTitle(title) {
  const t = String(title || "").trim().toLowerCase();
  for (const [key, idx] of Object.entries(DAY_NAME_TO_INDEX)) {
    if (t.startsWith(key)) return idx;
  }
  return null;
}

function splitPlanSections(planText) {
  const text = String(planText || "");
  if (!text.trim()) return [];

  const lines = text.split("\n");
  const sections = [];
  let currentTitle = "";
  let currentContent = [];

  const flush = () => {
    const body = currentContent.join("\n").trim();
    if (body || currentTitle) {
      sections.push({
        title: currentTitle || "Introduction",
        body,
        type: classifyTitle(currentTitle || "Introduction"),
      });
    }
    currentContent = [];
  };

  for (const line of lines) {
    // V95.1 : ne capture QUE les titres niveau 1-2 (# ou ##). Les ### sont
    // utilises par la section "## 4. ALTERNATIVES PAR REPAS" pour delimiter
    // les sous-slots (### Petit-dejeuner). Avant V95.1, ### etait traite
    // comme un titre principal et coupait la section en 5 morceaux vides
    // → le mapper trouvait "alternatives" avec body="" et parseSlotAlternatives
    // renvoyait []. Resultat : meal.alternatives jamais peuple cote app cliente.
    const md = line.match(/^#{1,2}\s+(.+)$/);
    if (md) {
      flush();
      currentTitle = md[1].trim();
      continue;
    }
    if (isUppercaseHeader(line)) {
      // V97.13.31 : si on est DEJA dans une section type 'supplements' ou
      // 'protocol', les lignes en MAJUSCULES sont des sous-titres internes
      // (noms de supplements : "VITAMINE D3 + K2", "ZINC BISGLYCINATE"...).
      // Elles ne doivent PAS couper la section, sinon le body de supplements
      // se vide et parseSupplementEntriesStructured renvoie 0 entries.
      // Resultat avant fix : 0 compléments cote app cliente malgre 6 supplements
      // detailles dans le plan markdown. Cf bug Camille V97.13.30.
      const currentType = classifyTitle(currentTitle || "");
      if (currentType === "supplements" || currentType === "protocol") {
        currentContent.push(line);
        continue;
      }
      flush();
      currentTitle = line.trim();
      continue;
    }
    currentContent.push(line);
  }
  flush();

  return sections;
}

/** Trouver la première section d'un type donné. */
function findSection(sections, type) {
  return sections.find((s) => s.type === type) || null;
}

/** Trouver toutes les sections d'un type. */
function findAllSections(sections, type) {
  return sections.filter((s) => s.type === type);
}

// ─── Locale / mode ────────────────────────────────────────────────────────

function resolveLocale(client) {
  const lang = String(client?.langue || "FR").toUpperCase();
  return lang === "EN" ? "en" : "fr";
}

// V94.36 : le mode app cliente doit refleter le PACK du client (oneshot_180,
// suivi_6m...) et NON pas le type de la consultation actuelle (initiale vs
// follow-up). Une cliente en pack suivi 6 mois dont la 1ere consultation a
// is_followup=false etait incorrectement mappee en 'oneshot'. Fix: on delegue
// a getNutritionPlanMode(client) qui lit client.packType (deja fiable cote
// SaaS depuis V80).
function resolveMode(client) {
  return getNutritionPlanMode(client);
}

// ─── 1. intro_data ────────────────────────────────────────────────────────
//
// Greeting : "Bonjour <prenom>," (locale-aware).
// Body : on prend la section "intro" du plan si présente, sinon on
// concatène observations + nutritional_observations comme fallback.
// Tous les champs littéraires (eyebrow, greeting_tagline, pull_quote,
// tailored_points, signature_phrase) restent undefined en V1.

function buildIntroData(client, consultation, sections) {
  const locale = resolveLocale(client);
  const prenom = (client?.prenom || "").trim();

  const defaultGreeting = locale === "fr"
    ? (prenom ? `Bonjour ${prenom},` : "Bonjour,")
    : (prenom ? `Hello ${prenom},` : "Hello,");

  // V94.47 : SOURCE PRIORITAIRE — intro_letter genere par IA + validee
  // par Anissa via l'onglet "Lettre" de NutritionConsultation. Si present,
  // on l'utilise tel quel (pas de filtrage looksLikeRawProfileData : Anissa
  // a deja valide le contenu).
  // V97.13.38 : greeting et signature aussi pris depuis intro_letter si fournis
  // (avant : hardcoded). Permet l'edition inline manuelle dans la modal Apercu.
  const introLetter = consultation?.intro_letter;
  const introLetterBody = typeof introLetter === "string"
    ? splitParagraphs(cleanForApp(introLetter))
    : Array.isArray(introLetter?.body)
      ? introLetter.body.map((paragraph) => String(paragraph || "").trim()).filter(Boolean)
      : typeof introLetter?.body === "string"
        ? splitParagraphs(cleanForApp(introLetter.body))
        : [];
  if (introLetterBody.length > 0) {
    const customGreeting = typeof introLetter.greeting === "string" ? introLetter.greeting.trim() : "";
    const customSignature = typeof introLetter.signature === "string" ? introLetter.signature.trim() : "";
    return {
      greeting: customGreeting || defaultGreeting,
      body: introLetterBody,
      pull_quote: typeof introLetter.pull_quote === "string" && introLetter.pull_quote.trim()
        ? introLetter.pull_quote.trim()
        : undefined,
      tailored_points: Array.isArray(introLetter.tailored_points)
        ? introLetter.tailored_points
            .map((p, i) => ({
              id: `tp-${i + 1}`,
              title: String(p?.title || "").trim(),
              detail: String(p?.detail || "").trim(),
            }))
            .filter((p) => p.title && p.detail)
        : undefined,
      signature: customSignature || "Anissa",
      coach_name: "Anissa",
      coach_role: locale === "fr" ? "Ta nutritionniste" : "Your nutritionist",
    };
  }
  // Variable conservee pour les fallbacks legacy ci-dessous
  const greeting = defaultGreeting;

  // FALLBACK legacy — sources d'intro existantes :
  //   1. Section "intro" du plan (vraie intro narrative écrite par Anissa)
  //   2. nutritional_observations (résumé Anissa, plus narratif)
  //   3. observations (souvent données brutes type profil cliente — fallback)
  const introSection = findSection(sections, "intro");
  const candidates = [
    introSection?.body,
    consultation?.nutritional_observations,
    consultation?.observations,
  ].filter(Boolean);

  // On prend la 1ère source qui ne ressemble PAS à des données brutes.
  let introBodySource = "";
  for (const c of candidates) {
    if (!looksLikeRawProfileData(c)) {
      introBodySource = c;
      break;
    }
  }

  // Nettoie + split en paragraphes + filtre ceux qui sont des données brutes.
  const body = splitParagraphs(cleanForApp(introBodySource))
    .filter((p) => !looksLikeRawProfileData(p));

  // Pas de fallback raw : si tous les paragraphes ressemblent à des données
  // sensibles (profil médical), on préfère une intro sobre (greeting +
  // signature seulement) plutôt que d'exposer des informations privées.

  return {
    greeting,
    body,
    signature: "Anissa",
    coach_name: "Anissa",
    coach_role: locale === "fr" ? "Ta nutritionniste" : "Your nutritionist",
    // eyebrow, greeting_tagline, pull_quote, tailored_points, coach_avatar_url
    // → undefined si pas de intro_letter (V94.47 priorise)
  };
}

// ─── 2. strategy_data ─────────────────────────────────────────────────────
//
// Source : section "strategy" du plan. Format attendu :
//   <paragraphes essentiels>
//   - bullet takeaway 1
//   - bullet takeaway 2
//   Pilier 1 : description
//   Pilier 2 : description
//
// Heuristique :
//   - paragraphes (lignes non bullet, non labeled) → essential[]
//   - bullets simples (- foo) → takeaways[]
//   - paires "Label : description" → pillars[]

function buildStrategyData(client, consultation, sections) {
  const locale = resolveLocale(client);

  // V97.13.41 : override editorial manuel (Phase A3 option C). Anissa edite
  // les piliers strategie depuis la modal Apercu app cote SaaS. Stocke dans
  // consultation.editorial_overrides.strategy. Si present, prime sur le
  // parsing automatique du markdown. Anissa garde la possibilite de revenir
  // au parsing en effacant l'override.
  const override = consultation?.editorial_overrides?.strategy || null;
  if (override && Array.isArray(override.pillars) && override.pillars.length > 0) {
    const pillarSeen = new Set();
    return {
      header_title: locale === "fr" ? "Ta stratégie" : "Your strategy",
      subtitle: typeof override.subtitle === "string" && override.subtitle.trim()
        ? override.subtitle.trim()
        : (locale === "fr" ? "Comprendre comment ton plan agit." : "Understand how your plan works."),
      essential: Array.isArray(override.essential)
        ? override.essential.map((s) => String(s || "").trim()).filter(Boolean)
        : [],
      pillars: override.pillars.map((p) => ({
        id: uniqueId(slugify(p.title || ""), pillarSeen),
        title: String(p?.title || "").trim(),
        description: String(p?.description || "").trim(),
      })).filter((p) => p.title && p.description),
      takeaways_title: locale === "fr" ? "Ce que tu dois retenir" : "What you should remember",
      takeaways: Array.isArray(override.takeaways)
        ? override.takeaways.map((s) => String(s || "").trim()).filter(Boolean)
        : [],
    };
  }

  // Sources : section "strategy" canonique + toutes les sections "soft"
  // détectées comme strategy_extra (BESOINS CALORIQUES, MACRONUTRIMENTS,
  // POINTS D'ATTENTION, APPROCHE ANTI-INFLAMMATOIRE, OPTIMISATION MICROBIOTE,
  // GESTION ÉNERGÉTIQUE, TIMING, HYDRATATION, etc.).
  const main = findSection(sections, "strategy");
  const extras = findAllSections(sections, "strategy_extra");

  const allBlocks = [main, ...extras].filter(Boolean);
  const fullText = allBlocks.map((s) => cleanForApp(s.body || "")).join("\n\n");

  // Bullets (takeaways) — concaténation de tous les bullets trouvés
  const takeaways = parseBulletLines(fullText)
    .map((t) => cleanForApp(t))
    .filter(Boolean)
    .slice(0, 6);

  // Pillars : on prend chaque section strategy_extra comme un pilier potentiel
  // (titre = nom de section, description = 1ère phrase ou bullet).
  const pillarSeen = new Set();
  let pillars = extras.slice(0, 3).map((sec) => {
    const cleanedBody = cleanForApp(sec.body || "");
    const firstBullet = parseBulletLines(cleanedBody)[0];
    const firstSentence = cleanedBody.split(/[.\n]/).map((s) => s.trim()).filter(Boolean)[0];
    const description = cleanForApp(firstBullet || firstSentence || "");
    return {
      id: uniqueId(slugify(sec.title), pillarSeen),
      title: sec.title,
      description,
    };
  }).filter((p) => p.description);

  // Fallback 1 : si pas de strategy_extra, on extrait les paires de la section
  // strategy canonique (format Anissa standard).
  if (pillars.length === 0 && main?.body) {
    const pairs = parseLabeledLines(cleanForApp(main.body));
    pillars = pairs
      .filter((p) => p.label && p.value && p.value.length > 5)
      .slice(0, 3)
      .map((p) => ({
        id: uniqueId(slugify(p.label), pillarSeen),
        title: p.label,
        description: cleanForApp(p.value),
      }));
  }

  // V94.70 : fallback 2 — l'IA peut produire des bullets sans format
  // "Label : valeur" exploitable par parseLabeledLines (regex strict). Plutot
  // que d'echouer en "Pas de piliers detectes" cote enrichissement, on
  // convertit chaque bullet en pilier en splittant sur le 1er separateur
  // raisonnable (": " > " — " > " - ") ou en utilisant la 1ere phrase courte
  // comme titre. C'est moins beau qu'un format strict mais ca evite les
  // crashs d'enrichissement quand le prompt n'a pas ete strictement suivi.
  if (pillars.length === 0 && main?.body) {
    const bullets = parseBulletLines(cleanForApp(main.body))
      .map((b) => cleanForApp(b))
      .filter((b) => b.length > 10);
    pillars = bullets.slice(0, 3).map((bullet) => {
      // Cherche un separateur naturel pour title/description.
      let title = bullet;
      let description = "";
      const sepMatch = bullet.match(/^(.{3,40}?)\s*[:—–-]\s+(.+)$/);
      if (sepMatch) {
        title = sepMatch[1].trim();
        description = sepMatch[2].trim();
      } else {
        // Pas de separateur : utilise les 4-6 premiers mots comme titre,
        // le reste comme description.
        const words = bullet.split(/\s+/);
        if (words.length > 6) {
          title = words.slice(0, 5).join(" ");
          description = words.slice(5).join(" ");
        } else {
          title = bullet;
          description = bullet; // titre = description si bullet court
        }
      }
      return {
        id: uniqueId(slugify(title), pillarSeen),
        title: title.slice(0, 80),
        description: description || title,
      };
    });
  }

  // Essential = paragraphes hors bullets/pairs (pris de la section main de
  // préférence, sinon des extras). Cleané + filtré.
  const essentialSource = main?.body
    ? cleanForApp(main.body)
    : extras.map((s) => cleanForApp(s.body || "")).join("\n\n");

  const essential = essentialSource
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => {
      if (!p) return false;
      // Skip si c'est juste un séparateur/markdown leftover
      if (/^[-_*\s]+$/.test(p)) return false;
      // Skip si paragraphe = données brutes type profil
      if (looksLikeRawProfileData(p)) return false;
      const lines = p.split("\n").map((l) => l.trim()).filter(Boolean);
      const isAllBulletOrPair = lines.every((l) =>
        /^([—•*·-]|\d+[.)])\s+/.test(l) || /^[A-Za-zéèàùûîâôçêŒœ][^:]{2,60}?\s*:\s*.+$/.test(l)
      );
      return !isAllBulletOrPair;
    })
    .slice(0, 3);

  return {
    header_title: locale === "fr" ? "Ta stratégie" : "Your strategy",
    subtitle: locale === "fr"
      ? "Comprendre comment ton plan agit."
      : "Understand how your plan works.",
    essential,
    pillars,
    takeaways_title: locale === "fr"
      ? "Ce que tu dois retenir"
      : "What you should remember",
    takeaways,
  };
}

// ─── 3. week_meals ────────────────────────────────────────────────────────
//
// V1 → V94.71 → V97.33 : on construit une "semaine type" puis on varie
// le menu jour apres jour via les alternatives (section 4 du markdown IA).
// Pattern : pool [meal_principal, ...alts] par slot, pioche modulo par jour.
// Le repas principal reste l'ancrage (apparait plusieurs fois sur 7 jours)
// et les alternatives nourissent la variation. Format attendu :
//   Petit-déjeuner : œuf brouillé + toast + fruit
//   Collation 10h : yaourt nature
//   Déjeuner : poulet + riz + légumes
//   Collation 16h : amandes
//   Dîner : poisson + patate douce + salade
//
// L'override par jour (mode hybride choisi par le user) viendra après
// migration : on lira consultation.week_overrides JSONB.

// V97.31 — Vocabulaire SUISSE ROMAND pour les labels affichés cliente.
// En Suisse romande : Dîner = MIDI, Souper = SOIR. "Déjeuner" en Suisse
// peut désigner le petit-déj (vocab confus). On force le vocabulaire CH
// pour aligner avec le contexte d'Anissa et éviter ambiguïté.
// Les regex captent les anciens labels FR (Déjeuner, Dîner pour soir)
// pour backward compat : un ancien plan généré avant V97.31 sera mappé
// correctement vers les slots lunch/dinner et affiché avec le bon label CH.
const SLOT_PATTERNS = [
  { slot: "morning_snack",   label_fr: "Collation matin", label_en: "Morning snack",
    re: /(collation\s*(?:matin|10h?))|morning\s*snack|mid\s*morning/i },
  { slot: "breakfast",       label_fr: "Petit-déjeuner",  label_en: "Breakfast",
    re: /(petit\s*[-\s]?d[ée]j(?:euner)?|breakfast|matin(?!e)|wake|morning\s*meal)/i },
  { slot: "afternoon_snack", label_fr: "Collation après-midi", label_en: "Afternoon snack",
    re: /(collation\s*(?:apr[èe]s[-\s]?midi|16h?))|afternoon\s*snack|tea\s*time/i },
  { slot: "evening_snack",   label_fr: "Collation soir",   label_en: "Evening snack",
    re: /(collation\s*soir|evening\s*snack|before\s*bed|nighttime)/i },
  // Les collations précèdent volontairement midi/soir : sinon le mot « midi »
  // de « Collation après-midi » ou « soir » de « Collation soir » capture le
  // libellé dans un slot de repas principal.
  { slot: "dinner",          label_fr: "Souper",           label_en: "Dinner",
    re: /(souper(?:\s*\(?\s*soir\s*\)?)?|d[îi]ner\s*\(?\s*soir\s*\)?|soir(?!ee)|dinner|evening\s*meal)/i },
  { slot: "lunch",           label_fr: "Dîner",            label_en: "Lunch",
    re: /(d[ée]jeuner|midi|lunch|noon|d[îi]ner)/i },
];

/** Variante stricte : ne retourne un slot que si la ligne courte matche un
 * pattern slot. Sert au fallback multi-ligne pour distinguer en-tête de slot
 * vs contenu. */
function classifyMealLabelStrict(label, locale) {
  for (const p of SLOT_PATTERNS) {
    if (p.re.test(label)) {
      return {
        slot: p.slot,
        slot_label: locale === "fr" ? p.label_fr : p.label_en,
      };
    }
  }
  // V95.1 : "Collation" simple aussi accepte en mode strict pour rester
  // coherent avec classifyMealLabel (cf. ci-dessus).
  if (/^collation$|^snack$|^go[uû]ter$/i.test(label.trim())) {
    return {
      slot: "afternoon_snack",
      slot_label: locale === "fr" ? "Collation" : "Snack",
    };
  }
  return null;
}

/** Strip des markers markdown bold/italic qui cassent parseLabeledLines.
 * Ex: "**Petit-déjeuner** : Porridge" → "Petit-déjeuner : Porridge". */
function stripMarkdownBold(text) {
  return String(text || "")
    .replace(/\*\*([^*\n]+)\*\*/g, "$1") // **bold**
    .replace(/__([^_\n]+)__/g, "$1")     // __bold__
    .replace(/(?<![*\w])\*([^*\n]+?)\*(?!\w)/g, "$1") // *italic* (single, isolé)
    .replace(/(?<![_\w])_([^_\n]+?)_(?!\w)/g, "$1");  // _italic_ (single, isolé)
}

/** Nettoie un texte destiné à l'app cliente :
 *  - retire les markers markdown bold/italic (**, __)
 *  - retire les séparateurs horizontaux (---, ___, ***)
 *  - réduit les espaces multiples
 *  - trim. */
function cleanForApp(text) {
  return stripMarkdownBold(text)
    .replace(/^\s*[-_*]{3,}\s*$/gm, "") // séparateurs horizontaux
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Indique si un paragraphe ressemble à des données brutes type
 *  "Genre : Homme Age : 35 ans Poids : 110 kg ..." (profil cliente).
 *  Heuristique : raw data si soit beaucoup de paires en absolu (>= 7),
 *  soit densité significative (>= 0.5 paire par 80 chars). */
function looksLikeRawProfileData(text) {
  if (!text) return false;
  const t = String(text);
  const colonGroups = (t.match(/[A-Za-zéèàùûîâôçêŒœ][^:]{2,40}\s*:\s*\S/g) || []).length;
  if (colonGroups < 5) return false;
  const density = colonGroups / Math.max(1, t.length / 80);
  return colonGroups >= 7 || density >= 0.5;
}

function normalizeFridgeKey(value) {
  return String(value || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function sanitizeFridgeLabel(value) {
  let label = cleanForApp(String(value || ""))
    .replace(/^[—\-•*·>]\s*/, "")
    .trim();
  if (!label) return "";
  // Une réponse utile préfixée par « Réponse : » garde son contenu. Une
  // question ou un champ brut de l'anamnèse n'est jamais une consigne frigo.
  label = label.replace(/^(?:r[ée]ponse|answer)\s*:\s*/i, "").trim();
  if (!label || /^(?:oui|non|ras|r\.a\.s\.?|n\/?a|non\s+renseign[ée]|inconnu|aucune\s+r[ée]ponse)$/i.test(label)) {
    return "";
  }
  if (/^(?:question|poids|taille|[âa]ge|date\s+de\s+naissance|allergies?|pathologies?|traitements?|sympt[oô]mes?|t[ée]l[ée]phone|e-?mail)\s*:/i.test(label)) {
    return "";
  }
  if (/^(?:[àa]\s+valider|information\s+manquante|non\s+disponible)\b/i.test(label)) return "";
  return label;
}

function sanitizeFridgeLabels(values) {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .map(sanitizeFridgeLabel)
    .filter((label) => {
      const key = normalizeFridgeKey(label);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function sanitizeFridgeCategories(categories) {
  const seen = new Set();
  const usedIds = new Set();
  return (Array.isArray(categories) ? categories : []).map((category) => {
    const items = (Array.isArray(category?.items) ? category.items : [])
      .map((item) => sanitizeFridgeLabel(item?.label))
      .filter((label) => {
        const key = normalizeFridgeKey(label);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((label) => ({ id: uniqueId(slugify(label), usedIds), label }));
    return { ...category, items };
  }).filter((category) => category.items.length > 0);
}

function sanitizeFridgeData(data, forbidden = []) {
  const essentials = sanitizeFridgeLabels((data?.essentials || []).map((item) => item?.label));
  let favorite = sanitizeFridgeCategories(data?.favorite);
  let limit = sanitizeFridgeCategories(data?.limit);
  const favoriteKeys = new Set([
    ...essentials.map(normalizeFridgeKey),
    ...favorite.flatMap((category) => category.items.map((item) => normalizeFridgeKey(item.label))),
  ]);
  const limitKeys = new Set(limit.flatMap(
    (category) => category.items.map((item) => normalizeFridgeKey(item.label)),
  ));
  const forbiddenKeys = new Set(sanitizeFridgeLabels(forbidden).map(normalizeFridgeKey));
  const neutralConflicts = new Set(
    [...favoriteKeys].filter((key) => limitKeys.has(key) && !forbiddenKeys.has(key)),
  );
  const removeFavorite = new Set([...neutralConflicts, ...forbiddenKeys]);
  const removeLimit = neutralConflicts;
  const filterCategories = (categories, blocked) => categories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => !blocked.has(normalizeFridgeKey(item.label))),
    }))
    .filter((category) => category.items.length > 0);
  favorite = filterCategories(favorite, removeFavorite);
  limit = filterCategories(limit, removeLimit);
  const usedEssentialIds = new Set();
  const cleanEssentials = essentials
    .filter((label) => !removeFavorite.has(normalizeFridgeKey(label)))
    .map((label) => ({ id: uniqueId(slugify(label), usedEssentialIds), label }));
  return { ...data, essentials: cleanEssentials, favorite, limit };
}

/** Construit la liste des repas d'un jour à partir d'un body texte.
 *  Supporte deux formats :
 *   A) "Petit-déjeuner : Porridge" sur une seule ligne
 *   B) "Petit-déjeuner" puis "Porridge" sur la ligne suivante
 *  Dédupe par slot (un seul repas par slot et par jour, premier servi). */
function buildMealsFromBody(body, locale) {
  if (!body) return [];
  const cleaned = stripMarkdownBold(body);

  // Format A : slot : title
  const pairs = parseLabeledLines(cleaned);
  const seenSlotA = new Set();
  const usedIdsA = new Set();
  const mealsA = [];
  for (const p of pairs) {
    const strict = classifyMealLabelStrict(p.label, locale);
    if (!strict) continue; // p.label n'est pas un slot reconnu → on ignore
    if (seenSlotA.has(strict.slot)) continue;
    seenSlotA.add(strict.slot);
    mealsA.push({
      id: uniqueId(`meal-${strict.slot}`, usedIdsA),
      slot: strict.slot,
      slot_label: strict.slot_label,
      title: p.value,
    });
  }
  if (mealsA.length > 0) return mealsA;

  // Format B (fallback) : slot tout seul, contenu sur la ligne suivante
  const lines = cleaned
    .split("\n")
    .map((l) => l.replace(/^[—\-•*·>]\s*/, "").trim())
    .filter(Boolean);

  const seenSlot = new Set();
  const usedIds = new Set();
  const mealsB = [];
  let pending = null; // { slot, slot_label } en attente de contenu

  for (const line of lines) {
    // Une ligne courte qui matche EXACTEMENT un slot connu = en-tête de slot.
    // Heuristique : <= 30 chars + match strict.
    if (line.length <= 30) {
      const strict = classifyMealLabelStrict(line, locale);
      if (strict) {
        pending = strict;
        continue;
      }
    }
    // Sinon : ligne de contenu pour le slot en attente
    if (pending && !seenSlot.has(pending.slot)) {
      seenSlot.add(pending.slot);
      mealsB.push({
        id: uniqueId(`meal-${pending.slot}`, usedIds),
        slot: pending.slot,
        slot_label: pending.slot_label,
        title: line,
      });
      pending = null;
    }
  }
  return mealsB;
}

function resolveBaseMealsSection(sections, locale, structureContract) {
  const canonical = findSection(sections, "meals");
  if (canonical) return canonical;
  if (!structureContract) return null;

  // Un titre éditable ne doit pas être une clé primaire. Avec un contrat de
  // structure explicite, on accepte une unique section renommée qui contient
  // réellement les trois repas de base. Plusieurs candidates restent
  // ambiguës : dans ce cas on ferme le fallback au lieu d'en choisir une.
  const candidates = sections
    .filter((section) => ["default", "week"].includes(section.type))
    .map((section) => ({ section, meals: buildMealsFromBody(section.body, locale) }))
    .filter(({ meals }) => {
      const slots = new Set(meals.map((meal) => meal.slot));
      return BASE_MEAL_SLOTS.every((slot) => slots.has(slot));
    });
  return candidates.length === 1 ? candidates[0].section : null;
}

function buildMealStructureState(days, structureContract) {
  if (!structureContract) return undefined;
  const expectedSlots = [...structureContract.expected_slots];
  const mappedSlots = [...new Set(
    (days || []).flatMap((day) => (day.meals || []).map((meal) => meal.slot)),
  )];
  const missingByDay = [];
  const unexpectedByDay = [];
  for (const day of days || []) {
    const slots = new Set((day.meals || []).map((meal) => meal.slot));
    const missing = expectedSlots.filter((slot) => !slots.has(slot));
    const unexpected = [...slots].filter((slot) => !expectedSlots.includes(slot));
    if (missing.length) missingByDay.push({ day: day.index, slots: missing });
    if (unexpected.length) unexpectedByDay.push({ day: day.index, slots: unexpected });
  }
  return {
    version: 1,
    id: structureContract.id,
    expected_slots: expectedSlots,
    mapped_slots: mappedSlots,
    complete: missingByDay.length === 0 && unexpectedByDay.length === 0,
    ...(missingByDay.length ? { missing_by_day: missingByDay } : {}),
    ...(unexpectedByDay.length ? { unexpected_by_day: unexpectedByDay } : {}),
  };
}

function withMealStructureState(days, structureContract) {
  const structure = buildMealStructureState(days, structureContract);
  return { days, ...(structure ? { structure } : {}) };
}

// V95 : Construit un index { slot → AlternativeMeal[] } à partir de la
// section "## 4. ALTERNATIVES PAR REPAS" du plan. Utilisé par buildWeekMeals
// pour décorer chaque meal.alternatives. Map vide si la section est absente
// (plans pré-V95) → backward compat naturelle.
//
// V95.4 : ajoute recipe aux alts si une recette a ete generee cote SaaS
// (mealRecipes[mealKey(slot, alt.title)]). Permet a l'app cliente de
// montrer la recette directement quand la cliente swap.
function buildAlternativesIndex(sections, locale, mealRecipes = {}) {
  const altSection = findSection(sections, "alternatives");
  if (!altSection?.body) return new Map();

  const groups = parseSlotAlternatives(altSection.body, locale);
  const index = new Map();

  for (const group of groups) {
    const slot = normalizeSlotLabelToSlot(group.slotLabel, locale);
    if (!slot) continue; // libellé non reconnu → drop ce groupe

    const usedAltIds = new Set(["primary"]);
    const items = group.items
      .map((it) => {
        if (!it.title || it.title.length < 3) return null;
        const recipe = mealRecipes[mealKey(slot, it.title)];
        const hasRecipe = recipe && (recipe.ingredients?.length || recipe.preparation?.length);
        return {
          id: uniqueId(slugify(it.title), usedAltIds),
          title: it.title,
          ...(it.hint ? { hint: it.hint } : {}),
          ...(hasRecipe ? { recipe } : {}),
        };
      })
      .filter(Boolean);

    if (items.length > 0) index.set(slot, items);
  }

  return index;
}

function buildWeekMeals(client, consultation, sections) {
  const locale = resolveLocale(client);
  const structureContract = resolveMealStructureContract(consultation);
  const dayLabels = locale === "fr" ? DAY_LABELS_FR : DAY_LABELS_EN;
  const dayShort = locale === "fr" ? DAY_SHORT_FR : DAY_SHORT_EN;
  const usedDayIds = new Set();

  // V94.42 : injection des recettes detaillees dans chaque meal.
  // mealRecipes est une map { mealKey → Recipe } stockee dans la consultation.
  // On injecte uniquement si l'IA/Anissa a effectivement enrichi le repas.
  const mealRecipes = (consultation && consultation.meal_recipes) || {};
  const attachRecipe = (m) => {
    const r = mealRecipes[mealKey(m.slot, m.title)];
    if (r && (r.ingredients?.length || r.preparation?.length)) {
      return { ...m, recipe: r };
    }
    return m;
  };

  // V95 : injection des alternatives par slot. Map vide si la section
  // "ALTERNATIVES PAR REPAS" n'a pas été produite par le prompt (plans pré-V95)
  // → meal.alternatives reste undefined, l'app cliente cache le bouton.
  // V95.4 : passe mealRecipes pour que les alts heritent de leur recette
  // si Anissa l'a generee cote SaaS.
  const altsBySlot = buildAlternativesIndex(sections, locale, mealRecipes);
  const attachAlternatives = (m) => {
    const alts = altsBySlot.get(m.slot);
    if (alts && alts.length > 0) return { ...m, alternatives: alts };
    return m;
  };

  // Mode A — JOUR PAR JOUR
  // Le plan contient des sections nommées LUNDI/MARDI/.../DIMANCHE (format
  // sport ou plan détaillé). On construit chaque jour à partir de sa section.
  const daySections = findAllSections(sections, "meals_day");
  if (daySections.length > 0) {
    // Map index -> meals
    const daysByIndex = new Map();
    for (const sec of daySections) {
      const idx = dayIndexFromTitle(sec.title);
      if (!idx) continue;
      const meals = buildMealsFromBody(sec.body, locale);
      // Si le même jour apparaît plusieurs fois (rare), on garde la première
      // occurrence non vide.
      if (!daysByIndex.has(idx) || (daysByIndex.get(idx).length === 0 && meals.length)) {
        daysByIndex.set(idx, meals);
      }
    }

    const days = dayLabels.map((label, i) => {
      const idx = i + 1;
      return {
        id: uniqueId(`day-${idx}`, usedDayIds),
        index: idx,
        label,
        short_label: dayShort[i],
        meals: (daysByIndex.get(idx) || []).map((m, j) => ({
          ...attachAlternatives(attachRecipe(m)),
          id: `day-${idx}-meal-${j + 1}`,
        })),
      };
    });
    return withMealStructureState(days, structureContract);
  }

  // Mode B — SEMAINE TYPE (avec rotation si plusieurs variantes)
  // V94.71 : on alterne entre la "JOURNEE TYPE" (section meals) et la
  // "JOURNEE TYPE ALTERNATIVE" (section meals_alt) pour eviter que la
  // cliente voie 7x les memes repas du Lundi au Dimanche. Si l'IA produit
  // plusieurs variantes alt, on rotate entre toutes (modulo).
  // S'il n'y a qu'une variante (cas plan minimal), comportement classique.
  const mealsSection = resolveBaseMealsSection(sections, locale, structureContract);
  const baseMeals = buildMealsFromBody(mealsSection?.body || "", locale);

  const altSections = findAllSections(sections, "meals_alt");
  const altVariants = altSections
    .map((s) => buildMealsFromBody(s.body, locale))
    .filter((m) => m.length > 0);

  const variants = [baseMeals, ...altVariants].filter((v) => v.length > 0);

  // Aucune variante : retour squelette vide (le diagnostic UI affichera
  // "0 repas" comme avant).
  if (variants.length === 0) {
    const days = dayLabels.map((label, i) => ({
      id: uniqueId(`day-${i + 1}`, usedDayIds),
      index: i + 1,
      label,
      short_label: dayShort[i],
      meals: [],
    }));
    return withMealStructureState(days, structureContract);
  }

  // Rotation : Lun/Mer/Ven/Dim = variant 0, Mar/Jeu/Sam = variant 1, etc.
  // Modulo permet d'absorber 1, 2 ou N variantes uniformement.
  //
  // V97.33 — Si une seule variante existe (cas standard : prompt section 3
  // produit UNE journee type), on enrichit la rotation en utilisant les
  // alternatives par slot (section 4 ALTERNATIVES PAR REPAS) pour
  // varier le menu jour apres jour. Sinon la cliente voit 7x les memes
  // repas, ce qui est peu credible vs le positionnement premium (1990 CHF).
  //
  // Pattern : pour chaque slot, on construit un pool [meal_principal,
  // ...alts]. Au jour i, on pioche pool[i % pool.length]. Le repas
  // principal reapparait regulierement comme ancrage. Les alternatives
  // restent disponibles cote app cliente (attachAlternatives reste
  // applique → la cliente peut toujours swap manuellement).
  const hasMultipleVariants = variants.length > 1;
  const days = dayLabels.map((label, i) => {
    const variant = variants[i % variants.length];

    const dayMeals = variant.map((m, j) => {
      // V97.33 — Rotation slot-by-slot uniquement quand on n'a qu'une
      // variante journee type (sinon les meals_alt font deja la variation).
      let chosenMeal = m;
      if (!hasMultipleVariants) {
        const slotAlts = altsBySlot.get(m.slot) || [];
        if (slotAlts.length > 0) {
          // Pool des candidats pour ce slot : repas principal + alternatives
          const slotPool = [m, ...slotAlts];
          const altPick = slotPool[i % slotPool.length];
          if (altPick) {
            // On garde le canal du slot (label affiche, slot key) et on
            // substitue le contenu (title, hint, recipe). Si altPick a sa
            // propre recette injectee via mealRecipes, attachRecipe la pose.
            chosenMeal = {
              ...altPick,
              slot: m.slot,
              slot_label: m.slot_label || altPick.slot_label,
            };
          }
        }
      }

      return {
        ...attachAlternatives(attachRecipe(chosenMeal)),
        id: `day-${i + 1}-meal-${j + 1}`,
      };
    });

    return {
      id: uniqueId(`day-${i + 1}`, usedDayIds),
      index: i + 1,
      label,
      short_label: dayShort[i],
      meals: dayMeals,
    };
  });

  return withMealStructureState(days, structureContract);
}

// ─── 4. rotation_data ─────────────────────────────────────────────────────
//
// Source : section "rotation". Le parser existant produit déjà
// [{title, items:[]}].

function buildRotationData(client, consultation, sections) {
  const locale = resolveLocale(client);
  const rotSection = findSection(sections, "rotation");
  const groups = parseRotationGroups(rotSection?.body || "");

  const usedCatIds = new Set();
  const categories = groups.map((g) => {
    const isPrimary = /prot[eé]ines?|f[eé]culents?/i.test(g.title);
    const usedItemIds = new Set();
    return {
      id: uniqueId(slugify(g.title), usedCatIds),
      title: g.title,
      primary: isPrimary || undefined,
      items: g.items.map((label) => ({
        id: uniqueId(slugify(label), usedItemIds),
        label,
      })),
    };
  });

  return {
    header_title: locale === "fr" ? "Adapter mes repas" : "Adapt my meals",
    // intro → undefined (post-migration)
    categories,
  };
}

// ─── 5. fridge_data ───────────────────────────────────────────────────────
//
// Deux sources possibles dans le SaaS :
//   a) consultation.fiche_frigo_json (JSONB legacy structuré)
//   b) section "fridge" du nutrition_plan (texte libre, bullets)
//
// Priorité : fiche_frigo_json si présent et exploitable, sinon fallback
// sur le parsing du markdown. À blanc, on parse aussi food_yes/food_limit
// si dispo (sections séparées).

function buildFridgeFromJson(json, locale) {
  if (!json || typeof json !== "object") return null;

  const usedEssIds = new Set();
  const essentials = Array.isArray(json.essentials)
    ? sanitizeFridgeLabels(json.essentials).map((label) => ({
        id: uniqueId(slugify(label), usedEssIds),
        label: String(label),
      }))
    : [];

  const buildCats = (input) => {
    if (!input) return [];
    // Si c'est un tableau (legacy : liste plate sans catégories), on emballe
    // dans une seule catégorie "À privilégier" / générique. Sinon les indices
    // 0, 1, 2, ... seraient pris pour des titres de catégories.
    if (Array.isArray(input)) {
      const usedItemIds = new Set();
      return input.length === 0 ? [] : [{
        id: "all",
        title: locale === "fr" ? "À privilégier" : "Favorite",
        items: sanitizeFridgeLabels(input)
          .map((label) => ({
            id: uniqueId(slugify(label), usedItemIds),
            label,
          })),
      }];
    }
    if (typeof input !== "object") return [];
    const usedCatIds = new Set();
    return Object.entries(input).map(([title, items]) => {
      const usedItemIds = new Set();
      return {
        id: uniqueId(slugify(title), usedCatIds),
        title,
        items: sanitizeFridgeLabels(items).map((label) => ({
          id: uniqueId(slugify(label), usedItemIds),
          label: String(label),
        })),
      };
    });
  };

  const favorite = buildCats(json.a_privilegier || json.favorite || {});
  const limit = buildCats(json.a_limiter || json.limit || {});

  return {
    header_title: locale === "fr" ? "Ton frigo idéal" : "Your ideal fridge",
    essentials,
    essentials_title: locale === "fr"
      ? "Tes repères essentiels"
      : "Always have at home",
    favorite,
    limit,
  };
}

function buildFridgeFromText(client, consultation, sections, locale) {
  const fridgeSection = findSection(sections, "fridge");
  const fridgeBullets = parseBulletLines(fridgeSection?.body || "");

  // V94.64 : on appelle le builder canonique pour recuperer toFavor / toLimit
  // / forbidden — meme source de verite que la modal SaaS et le Word.
  // Ainsi, ce que la cliente voit dans son app = ce qu'Anissa voit dans la
  // modal Fiche Frigo = ce qui sort dans le Word. Coherence E2E garantie.
  const canonical = buildCanonicalFridgeData(client, consultation, sections);
  const yesBullets = canonical.toFavor || [];
  // limit côté app = toLimit du plan + forbidden (allergies/alimentsEvites form)
  let limitBullets = [...(canonical.toLimit || [])];
  const seen = new Set(limitBullets.map((l) => l.toLowerCase()));
  for (const item of canonical.forbidden || []) {
    if (!seen.has(item.toLowerCase())) {
      limitBullets.push(item);
      seen.add(item.toLowerCase());
    }
  }

  const usedEssIds = new Set();
  const essentials = sanitizeFridgeLabels(fridgeBullets).map((label) => ({
    id: uniqueId(slugify(label), usedEssIds),
    label,
  }));

  const buildSingleCat = (title, bullets) => {
    if (!bullets.length) return [];
    const usedItemIds = new Set();
    return [{
      id: slugify(title),
      title,
      items: sanitizeFridgeLabels(bullets).map((label) => ({
        id: uniqueId(slugify(label), usedItemIds),
        label,
      })),
    }];
  };

  return {
    header_title: locale === "fr" ? "Ton frigo idéal" : "Your ideal fridge",
    essentials,
    essentials_title: locale === "fr"
      ? "Tes repères essentiels"
      : "Always have at home",
    favorite: buildSingleCat(
      locale === "fr" ? "À privilégier" : "Favorite",
      yesBullets,
    ),
    limit: buildSingleCat(
      locale === "fr" ? "À limiter" : "Limit",
      limitBullets,
    ),
  };
}

function buildFridgeData(client, consultation, sections) {
  const locale = resolveLocale(client);

  // V97.13.43 Phase A3c — Override editorial manuel du frigo. Anissa peut
  // editer les listes "À privilégier" et "À limiter" depuis la modal Apercu.
  // Stocke dans consultation.editorial_overrides.fridge.
  // Structure : { to_favor: [string], to_limit: [string] }
  // Si fourni, ces 2 listes ecrasent les essentiels et limit calcules.
  const fridgeOverride = consultation?.editorial_overrides?.fridge || null;

  // V97.13.28 — accepte snake_case (Supabase) ET camelCase (store local)
  const ficheFrigoJsonAny = consultation?.fiche_frigo_json || consultation?.ficheFrigoJson;
  const fromJson = buildFridgeFromJson(ficheFrigoJsonAny, locale);
  let baseData;
  if (fromJson && (fromJson.essentials.length || fromJson.favorite.length || fromJson.limit.length)) {
    baseData = fromJson;
  } else {
    baseData = buildFridgeFromText(client, consultation, sections, locale);
  }

  // Applique l'override si fourni (apres le base build pour conserver
  // header_title, favorite, etc.).
  if (fridgeOverride) {
    if (Array.isArray(fridgeOverride.to_favor) && fridgeOverride.to_favor.length > 0) {
      const seen = new Set();
      baseData = {
        ...baseData,
        essentials: fridgeOverride.to_favor
          .map((label) => String(label || '').trim())
          .filter((label) => label && !seen.has(label.toLowerCase()) && seen.add(label.toLowerCase()))
          .map((label, i) => ({ id: `fav-${i + 1}`, label })),
      };
    }
    if (Array.isArray(fridgeOverride.to_limit) && fridgeOverride.to_limit.length > 0) {
      const seen = new Set();
      const usedItemIds = new Set();
      const items = fridgeOverride.to_limit
        .map((label) => String(label || '').trim())
        .filter((label) => label && !seen.has(label.toLowerCase()) && seen.add(label.toLowerCase()))
        .map((label) => ({ id: uniqueId(slugify(label), usedItemIds), label }));
      baseData = {
        ...baseData,
        limit: items.length > 0 ? [{
          id: 'editorial-limit',
          title: locale === 'fr' ? 'À limiter' : 'Limit',
          items,
        }] : [],
      };
    }
  }

  return sanitizeFridgeData(baseData, buildForbiddenList(client?.form || {}));
}

// ─── 6. protocols_data ────────────────────────────────────────────────────
//
// Source : consultation.supplements (texte structuré) + sections protocol
// du plan. Le parser existant produit [{name, fields:{moment,dosage,...}}].
// On groupe par moment de prise.

const MOMENT_GROUPS = [
  { id: "morning",
    re: /matin|jeun|r[eé]veil|au\s*lever|d[eé]but\s*de\s*journ[eé]e|breakfast|morning|wake|am\b/i,
    title_fr: "Le matin", title_en: "Morning" },
  { id: "afternoon",
    re: /midi|d[ée]jeuner|apr[èe]s[-\s]?midi|gouter|go[uû]ter|collation|afternoon|noon|lunch|pm\b/i,
    title_fr: "L'après-midi", title_en: "Afternoon" },
  { id: "evening",
    re: /soir|d[îi]ner|coucher|nuit|avant\s*de\s*dormir|evening|dinner|bed|night/i,
    title_fr: "Le soir", title_en: "Evening" },
];

function classifyMoment(momentText) {
  for (const g of MOMENT_GROUPS) {
    if (g.re.test(String(momentText || ""))) return g.id;
  }
  return "other";
}

function buildProtocolsData(client, consultation, sections) {
  const locale = resolveLocale(client);

  // Source principale : champ supplements
  // Source secondaire : sections protocol* du plan
  const sources = [
    consultation?.supplements,
    ...findAllSections(sections, "protocol").map((s) => s.body),
    ...findAllSections(sections, "supplements").map((s) => s.body),
  ].filter(Boolean);

  // Stratégie permissive en 2 passes :
  //
  //  Pass 1 — exclut les faux positifs structurels (titres de sections, TOC,
  //  noms de doc) qui sont captés à tort par parseSupplementEntriesStructured
  //  (heuristique "ligne courte en MAJUSCULES" → bruit possible).
  //
  //  Pass 2 — préfère les entries qui ont au moins un field structuré
  //  (Sources/Dosage/Moment/Justification/Durée/Interactions). MAIS si AUCUN
  //  complément n'a de field (cas plans simples non structurés), on garde
  //  tous les noms qui ont passé Pass 1 — l'app cliente affichera juste les
  //  noms sans détails plutôt que rien.

  const isPlausibleSupplementName = (entry) => {
    if (!entry?.name) return false;
    const n = entry.name.trim();
    // Rejet : commence par un nombre + point/parenthèse → TOC ("1. ANALYSE")
    if (/^\d+\s*[.)-]/.test(n)) return false;
    // Rejet : titres de document ("PLAN NUTRITION...", "PLAN ALIMENTAIRE...")
    if (/^plan\s+\w+/i.test(n)) return false;
    // Rejet : titres très évidents (CONSEILS, AJUSTEMENTS, OBSERVATIONS,
    // OPTIMISATION, ANALYSE, PRINCIPES) — sections, pas compléments
    if (/^(conseils?|ajustements?|observations?|optimisation|analyse|principes?|recommandations?|introductions?|conclusion|cl[oô]ture)\b/i.test(n)) return false;
    // Rejet : un seul mot trop court probable bruit (sauf marques connues — on garde 4+)
    if (n.length < 4) return false;
    return true;
  };

  const hasStructuredField = (entry) => {
    const f = entry?.fields || {};
    return !!(f.dosage || f.moment || f.sources || f.justification || f.duree || f.interactions);
  };

  const passOne = sources
    .flatMap((src) => parseSupplementEntriesStructured(src))
    .filter(isPlausibleSupplementName);

  const withFields = passOne.filter(hasStructuredField);
  const allEntries = withFields.length > 0 ? withFields : passOne;

  // Grouper par moment de prise. On essaie d'abord le champ `moment`, puis on
  // se rabat sur une recherche dans le texte concaténé de tous les fields
  // (parfois Anissa met le timing dans `dosage` ou `interactions`).
  const groupBuckets = new Map();
  const fingerprintByItemId = new Map();
  const seenFingerprints = new Set();

  for (const entry of allEntries) {
    if (!entry?.name) continue;
    const fingerprint = supplementEntryFingerprint(entry);
    if (seenFingerprints.has(fingerprint)) continue;
    seenFingerprints.add(fingerprint);
    const itemId = `supp-${sha256HexSync(fingerprint)}`;
    const conflictingFingerprint = fingerprintByItemId.get(itemId);
    if (conflictingFingerprint && conflictingFingerprint !== fingerprint) {
      throw new Error("Identité de complément ambiguë. Compléments à valider avec Anissa.");
    }
    fingerprintByItemId.set(itemId, fingerprint);
    const fields = entry.fields || {};
    const allText = [
      fields.moment, fields.dosage, fields.interactions, fields.justification, fields.duree,
    ].filter(Boolean).join(" ");
    let groupId = classifyMoment(fields.moment);
    if (groupId === "other") groupId = classifyMoment(allText);

    if (!groupBuckets.has(groupId)) groupBuckets.set(groupId, []);
    groupBuckets.get(groupId).push({
      id: itemId,
      name: entry.name,
      benefit: fields.justification,
      timing_detail: fields.moment,
      dose: fields.dosage,
    });
  }

  // Si tous les compléments sont tombés dans "other" (aucun timing reconnu),
  // on les regroupe sous un seul groupe générique sans label de moment —
  // plus propre côté app cliente que d'afficher "Autre moment".
  const ids = [...groupBuckets.keys()];
  const supplementsIntroOverride = (consultation?.editorial_overrides?.supplements_intro || '').trim();
  if (ids.length === 1 && ids[0] === "other") {
    const items = groupBuckets.get("other");
    const groups = items.length ? [{
      id: "all",
      title: locale === "fr" ? "Vos compléments" : "Your supplements",
      items,
    }] : [];
    return {
      header_title: locale === "fr" ? "Vos compléments" : "Your supplements",
      ...(supplementsIntroOverride ? { intro: supplementsIntroOverride } : {}),
      groups: applySupplementEditorialOverride(
        groups,
        consultation?.editorial_overrides?.supplements,
      ),
    };
  }

  const titleFor = (id) => {
    const g = MOMENT_GROUPS.find((x) => x.id === id);
    if (g) return locale === "fr" ? g.title_fr : g.title_en;
    return locale === "fr" ? "Autre moment" : "Other";
  };

  // Ordre stable : morning, afternoon, evening, other
  const order = ["morning", "afternoon", "evening", "other"];
  const groups = order
    .filter((id) => groupBuckets.has(id))
    .map((id) => ({
      id,
      title: titleFor(id),
      items: groupBuckets.get(id),
    }));

  // V97.13.42 Phase A3b — Override editorial manuel de l intro complements.
  // Anissa peut editer le texte d intro depuis la modal Apercu app cote SaaS.
  // Stocke dans consultation.editorial_overrides.supplements_intro.
  // Note : l enrichissement IA (applyEnrichmentToPlan) ecrit sur 'intro' au
  // niveau de protocols_data. L override manuel ici prime sur l enrichissement
  // (l override est le dernier mot d Anissa).
  return {
    header_title: locale === "fr" ? "Vos compléments" : "Your supplements",
    ...(supplementsIntroOverride ? { intro: supplementsIntroOverride } : {}),
    groups: applySupplementEditorialOverride(
      groups,
      consultation?.editorial_overrides?.supplements,
    ),
  };
}

// ─── Builder principal ────────────────────────────────────────────────────

/**
 * Construit le ClientPlan JSON à publier dans l'app cliente.
 *
 * @param {object} client       - ligne `clients` du SaaS
 * @param {object} consultation - ligne `nutrition_consultations` du SaaS
 * @returns {object}            - ClientPlan (sans id/published_at — set côté staging)
 */
export function buildClientAppPlanFromConsultation(client, consultation) {
  if (!client) throw new Error("clientAppMapper: client is required");
  if (!consultation) throw new Error("clientAppMapper: consultation is required");
  const mode = resolveMode(client);
  if (mode !== 'oneshot' && mode !== 'followup') {
    throw new Error('clientAppMapper: pack absent ou inconnu');
  }

  // V97.13.28 — dette technique : le store local retourne nutritionPlan
  // (camelCase) alors que Supabase utilise nutrition_plan (snake_case). Le
  // mapper était silencieusement cassé pour les consultations venant du store
  // local — sections splittées sur une chaîne vide → 0 piliers, 0 repas, etc.
  // Fix : accepter les deux conventions.
  const planText = consultation.nutrition_plan
    || consultation.nutritionPlan
    || consultation.plan_text
    || '';
  const sections = splitPlanSections(planText);

  const intro_data    = buildIntroData(client, consultation, sections);
  const strategy_data = buildStrategyData(client, consultation, sections);
  let week_meals    = buildWeekMeals(client, consultation, sections);
  // V97.13.44 Phase A3d — Override editorial manuel des repas day1.
  // Anissa edite les titres des repas + alternatives depuis la modal Apercu.
  // Stocke dans consultation.editorial_overrides.meals = [{ slot, title,
  // alternatives: [{ title }] }]. On applique a chaque jour le meme override
  // (semaine type repete pour tous les jours).
  //
  // V97.116 — Les alternatives sont desormais APP-ONLY : le prompt du plan ne
  // les ecrit plus (section "## 4. ALTERNATIVES PAR REPAS" retiree), donc
  // buildAlternativesIndex renvoie vide sur les nouveaux plans. L'override
  // devient LA SOURCE des alternatives app : si un override fournit des
  // alternatives pour un slot, le repas app recoit CES alternatives (CREATION,
  // plus seulement renommage). Les recettes sont rattachees via le meme
  // mecanisme que buildAlternativesIndex : mealRecipes indexe par
  // mealKey(slot, title). Retro-compat : sur un plan ancien qui contient encore
  // la section (ou dont l'override ne fait que renommer), le comportement reste
  // coherent — un override sans alternatives ne touche pas celles deja parsees.
  const mealsOverride = consultation?.editorial_overrides?.meals;
  if (Array.isArray(mealsOverride) && mealsOverride.length > 0 && week_meals?.days?.length) {
    const mealRecipesForAlts = (consultation && consultation.meal_recipes) || {};
    const overrideBySlot = new Map();
    for (const ov of mealsOverride) {
      const slot = String(ov?.slot || '').trim();
      if (!slot) continue;
      overrideBySlot.set(slot, {
        title: typeof ov.title === 'string' ? ov.title.trim() : null,
        alternatives: Array.isArray(ov.alternatives) ? ov.alternatives : null,
        // Description du plat principal : appliquée seulement pour un repas
        // enregistré par l'éditeur Semaine (clé explicite + marqueur). Les
        // overrides déjà publiés gardent exactement leur rendu.
        primaryHint: ov.edited_in === 'week_editor' && Object.hasOwn(ov, 'hint') && typeof ov.hint === 'string'
          ? ov.hint.trim()
          : undefined,
      });
    }
    week_meals = {
      ...week_meals,
      days: week_meals.days.map((day) => ({
        ...day,
        meals: (day.meals || []).map((m) => {
          const ov = overrideBySlot.get(String(m.slot || ''));
          if (!ov) return m;
          let next = { ...m };
          if (ov.title) {
            next.title = ov.title;
            // buildWeekMeals rattache d'abord la recette au titre provenant du
            // markdown. Lorsqu'Anissa remplace ce titre dans l'aperçu, il faut
            // refaire le rattachement avec la clé canonique du nouveau menu.
            const mainRecipe = mealRecipesForAlts[mealKey(String(m.slot || ''), ov.title)];
            if (mainRecipe && (mainRecipe.ingredients?.length || mainRecipe.preparation?.length)) {
              next.recipe = mainRecipe;
            } else {
              delete next.recipe;
            }
          }
          if (ov.primaryHint !== undefined) {
            if (ov.primaryHint) next.hint = ov.primaryHint;
            else delete next.hint;
          }
          if (ov.alternatives && Array.isArray(ov.alternatives)) {
            const slot = String(m.slot || '');
            const usedAltIds = new Set(["primary"]);
            const builtAlts = ov.alternatives
              .map((altOv, i) => {
                const title = typeof altOv?.title === 'string' ? altOv.title.trim() : '';
                if (!title || title.length < 3) return null;
                // Preserve un eventuel hint deja present sur l'alt parsee du plan
                // (retro-compat avec les plans qui portaient encore la section 4).
                // Une clé `hint` explicite fait foi (chaîne vide = pas de
                // description) : le repli positionnel ne sert qu'aux anciens
                // overrides sans clé, sinon un retrait décalerait les textes.
                const prev = Array.isArray(next.alternatives) ? next.alternatives[i] : null;
                const hint = altOv && Object.hasOwn(altOv, 'hint') && typeof altOv.hint === 'string'
                  ? (altOv.hint.trim() || undefined)
                  : (prev?.hint || undefined);
                // Rattache la recette via le meme mecanisme que buildAlternativesIndex.
                const recipe = mealRecipesForAlts[mealKey(slot, title)];
                const hasRecipe = recipe && (recipe.ingredients?.length || recipe.preparation?.length);
                return {
                  id: uniqueId(slugify(title), usedAltIds),
                  title,
                  ...(hint ? { hint } : {}),
                  ...(hasRecipe ? { recipe } : {}),
                };
              })
              .filter(Boolean);
            if (builtAlts.length > 0) next.alternatives = builtAlts;
          }
          return next;
        }),
      })),
    };
  }
  // Rotation 4 semaines : contrat additif et compact. `days` reste la semaine
  // de référence pour les clients historiques ; le nouveau client applique
  // seulement les choix déjà validés, sans dupliquer 28 jours de contenu.
  const publishedFourWeekRotation = buildPublishedFourWeekRotation(
    week_meals,
    consultation?.editorial_overrides?.four_week_rotation,
  );
  if (publishedFourWeekRotation) {
    week_meals = {
      ...week_meals,
      rotation: publishedFourWeekRotation,
    };
  }
  // Meal prep : seul un contenu explicitement validé par Anissa ET toujours
  // cohérent avec les recettes visibles est joint au DTO client. Les champs
  // de workflow éditorial (status, source_revision) restent dans le SaaS.
  const publishedMealPrepV2 = buildPublishedModularMealPrep(
    week_meals,
    consultation?.editorial_overrides?.meal_prep_v2,
  );
  const publishedMealPrep = publishedMealPrepV2 || buildPublishedMealPrep(
    week_meals,
    consultation?.editorial_overrides?.meal_prep,
  );
  week_meals = {
    ...week_meals,
    ...(publishedMealPrep ? { prep: publishedMealPrep } : {}),
  };
  const rotation_data = buildRotationData(client, consultation, sections);
  const fridge_data   = buildFridgeData(client, consultation, sections);
  const protocols_data= buildProtocolsData(client, consultation, sections);

  // Timeline cliente reconstruite par liste blanche. Le snapshot professionnel
  // peut contenir clinical_name, recommendations, supplements, doses ou notes
  // retrospectives : aucun de ces champs ne doit quitter le SaaS.
  const journey_phases = buildPublishedJourneyPhases(consultation?.protocol_phases);

  return {
    client_id: client.id,
    status: "draft",
    locale: resolveLocale(client),
    mode,
    title: client.prenom
      ? (resolveLocale(client) === "fr"
          ? `Plan de ${client.prenom}`
          : `${client.prenom}'s plan`)
      : undefined,
    objective: client?.form?.objectif
      || client?.form?.objective
      || client?.form?.objectifPrincipalNutrition
      || undefined,
    published_version: 0,
    journey_phases,
    sections: {
      intro_data,
      strategy_data,
      week_meals,
      rotation_data,
      fridge_data,
      protocols_data,
    },
  };
}

const PUBLIC_PHASE_STATUSES = new Set(['upcoming', 'active', 'completed']);

/**
 * DTO public minimal d'un parcours deja valide par Anissa.
 * Toute phase mal formee ferme la timeline entiere. Les champs professionnels
 * sont ignores par construction.
 */
export function buildPublishedJourneyPhases(value) {
  if (!value || value.skipped === true) return null;
  if (typeof value !== 'object') return null;
  if (!Array.isArray(value.phases) || value.phases.length === 0) return null;

  const phases = [];
  const publicOrders = new Set();
  for (const phase of value.phases) {
    if (!phase || typeof phase !== 'object') return null;
    if (typeof phase.id !== 'string' || !phase.id) return null;
    if (!Number.isInteger(phase.order) || phase.order < 1) return null;
    if (publicOrders.has(phase.order)) return null;
    publicOrders.add(phase.order);
    if (typeof phase.client_name !== 'string' || !phase.client_name.trim()) return null;
    if (!PUBLIC_PHASE_STATUSES.has(phase.status)) return null;
    if (!Number.isInteger(phase.duration_weeks_min) || phase.duration_weeks_min < 0) return null;
    if (!Number.isInteger(phase.duration_weeks_max) || phase.duration_weeks_max < 0) return null;
    if (
      typeof phase.narrative_present !== 'string' ||
      typeof phase.narrative_past !== 'string' ||
      typeof phase.narrative_future !== 'string'
    ) return null;

    phases.push({
      // Identifiant public structurel : ne publie jamais l'id professionnel.
      id: `phase-${phase.order}`,
      order: phase.order,
      client_name: phase.client_name.trim(),
      duration_weeks_min: phase.duration_weeks_min,
      duration_weeks_max: phase.duration_weeks_max,
      narrative_present: phase.narrative_present,
      narrative_past: phase.narrative_past,
      narrative_future: phase.narrative_future,
      status: phase.status,
      started_at: typeof phase.started_at === 'string' ? phase.started_at : null,
      completed_at: typeof phase.completed_at === 'string' ? phase.completed_at : null,
    });
  }

  return { phases };
}

// ─── Diagnostic helper (pour étape 2 : test sur 1 vraie consultation) ─────
//
// Renvoie un rapport texte des champs manquants/vides détectés, pour
// itérer sur le mapping avant migration DB.

export function diagnoseClientAppPlan(plan) {
  const issues = [];
  const s = plan?.sections || {};

  if (!s.intro_data?.body?.length) issues.push("intro.body: vide");
  if (!s.intro_data?.tailored_points?.length) issues.push("intro.tailored_points: vide (à ajouter post-migration)");

  if (!s.strategy_data?.essential?.length) issues.push("strategy.essential: vide");
  if (!s.strategy_data?.pillars?.length) issues.push("strategy.pillars: vide");
  if (!s.strategy_data?.takeaways?.length) issues.push("strategy.takeaways: vide");
  if (!s.strategy_data?.signature_phrase?.length) issues.push("strategy.signature_phrase: vide (à ajouter post-migration)");

  const totalMeals = (s.week_meals?.days || []).reduce((acc, d) => acc + (d.meals?.length || 0), 0);
  if (!totalMeals) issues.push("week_meals: aucun repas trouvé (vérifier section SEMAINE 1 — STRUCTURE ALIMENTAIRE)");
  const mealStructure = s.week_meals?.structure;
  if (mealStructure && mealStructure.complete !== true) {
    for (const entry of mealStructure.missing_by_day || []) {
      issues.push(`week_meals.structure: jour ${entry.day}, slots manquants: ${entry.slots.join(", ")}`);
    }
    for (const entry of mealStructure.unexpected_by_day || []) {
      issues.push(`week_meals.structure: jour ${entry.day}, slots non prévus: ${entry.slots.join(", ")}`);
    }
  }

  if (!s.rotation_data?.categories?.length) issues.push("rotation.categories: vide (vérifier section ROTATION/SUBSTITUTIONS)");

  const fridgeCount = (s.fridge_data?.essentials?.length || 0)
                    + (s.fridge_data?.favorite?.length || 0)
                    + (s.fridge_data?.limit?.length || 0);
  if (!fridgeCount) issues.push("fridge_data: aucun item (vérifier section FICHE FRIGO ou fiche_frigo_json)");

  if (!s.protocols_data?.groups?.length) issues.push("protocols_data: aucun complément (vérifier consultation.supplements)");

  return {
    ok: issues.length === 0,
    issues,
    summary: {
      intro_paragraphs: s.intro_data?.body?.length || 0,
      strategy_pillars: s.strategy_data?.pillars?.length || 0,
      strategy_takeaways: s.strategy_data?.takeaways?.length || 0,
      week_total_meals: totalMeals,
      meal_structure_id: mealStructure?.id || null,
      meal_structure_complete: mealStructure?.complete ?? null,
      rotation_categories: s.rotation_data?.categories?.length || 0,
      fridge_items: fridgeCount,
      protocol_groups: s.protocols_data?.groups?.length || 0,
    },
  };
}
